import React from 'react';

import classNames from 'classnames';

import TriangleDown from 'Assets/icons/triangle-down.svg';
import TriangleUp from 'Assets/icons/triangle-up.svg';

/**
 * Delta sense. Most People measures are descriptive — a bigger peak headcount is
 * neither good nor bad — so their delta is neutral charcoal. Only two carry a
 * value judgement: more positions behind schedule is bad, more savings is good.
 */
export const SENSE_NEUTRAL = 'neutral';
export const SENSE_MORE_IS_BAD = 'more-is-bad';
export const SENSE_MORE_IS_GOOD = 'more-is-good';

function deltaColourClass(delta, sense) {
    if (sense === SENSE_NEUTRAL || delta === 0) {
        return 'tw-text-charcoal-50';
    }

    const favourable = sense === SENSE_MORE_IS_GOOD ? delta > 0 : delta < 0;

    return favourable ? 'tw-text-green' : 'tw-text-red';
}

/**
 * One signed movement against the compare-to version, shared by the KPI strip
 * and the movement panel so the two cannot render the same figure differently.
 *
 * A null delta renders nothing at all: "no comparable figure in the compared
 * version" is a real answer and must not appear as a zero movement. A caller
 * that needs to say so out loud renders its own placeholder rather than passing
 * a zero in.
 *
 * `inline` picks the display mode, and it has to be a prop rather than a class
 * the caller appends: Tailwind emits `.inline-flex` after `.flex`, so source
 * order decides and a call site cannot override it.
 *
 *  - `inline` (default) — an inline-flex box, which participates in a line box
 *    and therefore honours the `text-align: right` on `.inbud-numeric-cell`.
 *    Required inside a table cell; a block-level flex container ignores it.
 *  - `inline={false}` — a block-level flex container, as the KPI tile has always
 *    had. Inline there would let the row height shift and could drop a zero
 *    delta (which renders no triangle) off the baseline its siblings sit on.
 */
export default function PeopleDelta({ delta, format, sense = SENSE_NEUTRAL, inline = true, className }) {
    if (typeof delta !== 'number') {
        return null;
    }

    const Triangle = delta < 0 ? TriangleDown : TriangleUp;
    const sign = delta > 0 ? '+' : delta < 0 ? '−' : '';

    return (
        <span className={classNames(inline ? 'tw-inline-flex' : 'tw-flex', 'tw-items-center tw-gap-x-1', deltaColourClass(delta, sense), className)}>
            {delta !== 0 && <Triangle className="tw-w-3" />}
            <span className="tw-text-xs">
                {sign}
                {format(Math.abs(delta))}
            </span>
        </span>
    );
}
